import React, {useState, useRef} from 'react';
import {Link, useParams} from "react-router-dom";
import DisplayMetaWithImport from "../components/DisplayMetaWithImport.comp";
import {formatDate} from "../../../utils/utils";

import {
    v3Level11Rubric
} from "../../../services/metadata.service";

// name: display on preview top
// key: column# of excel sheet
// json_key: key for posting data to api
// key: column# of excel sheet (0-based) for "Level11 (Rubric)" sheet of DQ ClassARC v8.0
// NOTE: column 0 of the sheet is "Rubric#", stored as the unique `code` field.
const columns = [
    {name: 'Code (Rubric#)', key: 0, json_key: 'code', data_key: 'code'},
    {name: 'Name', key: 1, json_key: 'full_name', data_key: 'full_name'},
    {name: 'Definition', key: 2, json_key: 'definition', data_key: 'definition'},
    {name: 'Tier', key: 3, json_key: 'tier', data_key: 'tier'},
    {name: 'Element#', key: 4, json_key: 'element_no', data_key: 'element_no'},
    {name: 'Bloom Level', key: 5, json_key: 'bloom_level', data_key: 'bloom_level'},
    {name: 'Session#', key: 6, json_key: 'session_no', data_key: 'session_no'},
    {name: 'KSAB Dimension', key: 7, json_key: 'ksab_dimension', data_key: 'ksab_dimension'},
    {name: 'Unit#', key: 8, json_key: 'unit_no', data_key: 'unit_no'},
    {name: 'Unit Type', key: 9, json_key: 'unit_type', data_key: 'unit_type'},
    {name: 'DQCO#', key: 10, json_key: 'dqco_no', data_key: 'dqco_no'},
    {name: 'DQC#', key: 11, json_key: 'dqc_no', data_key: 'dqc_no'},
    {name: 'DQM#', key: 12, json_key: 'dqm_no', data_key: 'dqm_no'},
    {name: 'DQA#', key: 13, json_key: 'dqa_no', data_key: 'dqa_no'},
]

// v8.0 new columns are optional in the edit form (the modal defaults every field to required)
const fieldConfig = {
    full_name: { required: false },
    tier: { required: false },
    bloom_level: { required: false },
    ksab_dimension: { required: false },
    unit_type: { required: false },
}

const tableCols = [
    {
        label: 'Code',
        field: 'code', width: 150
    },
    {
        label: 'Name',
        field: 'full_name', width: 150
    },
    {
        label: 'Definition',
        field: 'definition', sort: 'disabled', width: 150
    },
    {
        label: 'Tier',
        field: 'tier', width: 120
    },
    {
        label: 'Element#',
        field: 'element_no', width: 150
    },
    {
        label: 'Bloom Level',
        field: 'bloom_level', width: 120
    },
    {
        label: 'Session#',
        field: 'session_no', width: 120
    },
    {
        label: 'KSAB Dimension',
        field: 'ksab_dimension', sort: 'disabled', width: 200
    },
    {
        label: 'Unit#',
        field: 'unit_no', width: 120
    },
    {
        label: 'Unit Type',
        field: 'unit_type', width: 150
    },
    {
        label: 'DQCO#',
        field: 'dqco_no', width: 120
    },
    {
        label: 'DQA#',
        field: 'dqa_no', width: 120
    },
    {
        label: 'Created',
        field: 'createdAt', width: 150
    },
    {
        label: 'Updated',
        field: 'updatedAt', width: 150
    },
    {
        label: 'Actions',
        field: 'actions', sort: 'disabled', searchable: false
    }
]


export default function Level11RubricPage() {

    const { url_param_str } = useParams();
    const search_query = url_param_str.split('=').length > 1 ? url_param_str.split('=')[1]  : '';

    const [serverPage, setServerPage] = useState(1);
    const [serverLimit, setServerLimit] = useState(10);
    const [serverTotal, setServerTotal] = useState(0);
    const [serverTotalPages, setServerTotalPages] = useState(1);
    const [serverSearch, setServerSearch] = useState(search_query || '');
    const [debouncedSearch, setDebouncedSearch] = useState(search_query || '');
    const debounceTimer = useRef(null);

    const handleServerSearchChange = (newSearch) => {
        setServerSearch(newSearch);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(newSearch);
            setServerPage(1);
        }, 400);
    };

    const fetchPage = async (page, limit, search) => {
        const result = await v3Level11Rubric.listData(page, limit, search);
        if (result && result.data) {
            const { total, page: pg, limit: lm, total_pages } = result.data;
            const clampedPage = total_pages > 0 && pg > total_pages ? total_pages : pg;
            setServerPage(clampedPage || 1);
            setServerLimit(lm || limit);
            setServerTotal(total || 0);
            setServerTotalPages(total_pages || 1);
        }
        return result;
    };

    const handleServerPageChange = (newPage) => { setServerPage(newPage); };
    const handleServerLimitChange = (newLimit) => { setServerPage(1); setServerLimit(newLimit); };
    const paginatedListAPI = async () => fetchPage(serverPage, serverLimit, debouncedSearch);

    const table_row__additional_rendering = (row) => {
        const create_data = formatDate(row.createdAt) + ',\n ' + (row.createdBy ? row.createdBy : '');
        row.createdAt = create_data;

        const update_data = formatDate(row.updatedAt) + ',\n ' + (row.updatedBy ? row.updatedBy : '');
        row.updatedAt = update_data;

        return row;
    }

    return (
        <DisplayMetaWithImport
            title={'Level11 Rubric'}
            modal_title={'Level11 Rubric - Data Preview'}
            sheet_name={'Level11 (Rubric)'}
            columns={columns}
            tableCols={tableCols}
            fieldConfig={fieldConfig}
            chunkSize={2000}
            tableSpRender={table_row__additional_rendering}
            search_query={search_query}
            listAPI={paginatedListAPI}
            postAPI={v3Level11Rubric.postData}
            deleteAPI={v3Level11Rubric.deleteEntity}
            updateAPI={v3Level11Rubric.updateEntity}
            preRemoveAPI={v3Level11Rubric.preRemoveCheck}
            serverSidePagination={true}
            serverPage={serverPage}
            serverLimit={serverLimit}
            serverTotal={serverTotal}
            serverTotalPages={serverTotalPages}
            onServerPageChange={handleServerPageChange}
            onServerLimitChange={handleServerLimitChange}
            serverSideSearch={true}
            serverSearch={serverSearch}
            onServerSearchChange={handleServerSearchChange}
            debouncedSearch={debouncedSearch}
        />
    );
}
